import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Dice5, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
      } else {
        await register(name.trim(), email.trim(), password);
      }
      toast.success("Welcome to your classroom");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const useDemo = async () => {
    setLoading(true);
    try {
      await login("teacher@school.edu", "teacher123");
      toast.success("Signed in as Demo Teacher");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen paper-bg flex flex-col">
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="w-full max-w-md card-paper p-7 sm:p-9 relative"
        >
          <div className="tape left-10 -top-3" />
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center rotate-3">
              <Dice5 className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">
                Teacher access
              </div>
              <h1 className="font-display text-2xl font-bold text-slate-900">
                {mode === "login" ? "Sign in" : "Create account"}
              </h1>
            </div>
          </div>

          <div className="flex bg-slate-100 rounded-xl p-1 mb-6" role="tablist">
            <button
              type="button"
              onClick={() => setMode("login")}
              data-testid="tab-login"
              className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${
                mode === "login" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              data-testid="tab-register"
              className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${
                mode === "register" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
              }`}
            >
              Register
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Your name
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="teacher-register-name-input"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-white focus:border-indigo-500 focus:outline-none text-slate-900"
                  placeholder="Ms. Rivera"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Email
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="teacher-login-email-input"
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-white focus:border-indigo-500 focus:outline-none text-slate-900"
                placeholder="you@school.edu"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Password
              </label>
              <input
                type="password"
                required
                minLength={6}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="teacher-login-password-input"
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-white focus:border-indigo-500 focus:outline-none text-slate-900"
                placeholder="At least 6 characters"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="teacher-login-submit-button"
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={useDemo}
              disabled={loading}
              data-testid="teacher-demo-login-button"
              className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 underline underline-offset-4 decoration-2 decoration-amber-300"
            >
              Use demo teacher account →
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
